import React from 'react';
import { useFormContext, useFieldArray, Controller } from 'react-hook-form';
import { Box, TextField, Typography, Button, IconButton, InputAdornment } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { HotelFormData, RenovationEntryData } from '@types';

const RenovationHistoryStep: React.FC = () => {
  const {
    control,
    register,
    formState: { errors },
  } = useFormContext<HotelFormData>();

  // useFieldArray manages the dynamic list state within react-hook-form
  const { fields, append, remove } = useFieldArray<HotelFormData, 'renovation_history'>({
    control,
    name: 'renovation_history',
    // keyName: 'id' // use 'id' from defaultValues if you have one, otherwise defaults to internal id
  });

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="h6" gutterBottom>
        Renovation History
      </Typography>
      <Box sx={{ maxHeight: 300, overflowY: 'auto', pr: 1 }}>
        {' '}
        {/* Add scroll if list gets long */}
        {fields.map((field, index) => (
          <Box
            // Use field.id provided by useFieldArray for stable keys
            key={field.id}
            sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2, p: 1.5, border: '1px solid lightgrey', borderRadius: 1 }}
          >
            <TextField
              label="Year"
              type="number"
              size="small"
              sx={{ width: '20%' }}
              InputProps={{ inputProps: { min: 1800, max: new Date().getFullYear() } }}
              // Register field with index
              {...register(`renovation_history.${index}.year` as const, {
                valueAsNumber: true, // Convert input to number
                min: { value: 1800, message: 'Invalid year' },
                max: { value: new Date().getFullYear(), message: 'Year cannot be in the future' },
              })}
              error={!!errors.renovation_history?.[index]?.year}
              helperText={errors.renovation_history?.[index]?.year?.message}
            />
            <TextField
              label="Description"
              size="small"
              sx={{ flexGrow: 1 }}
              {...register(`renovation_history.${index}.description` as const, {
                maxLength: { value: 200, message: 'Description too long (max 200 chars)' },
              })}
              error={!!errors.renovation_history?.[index]?.description}
              helperText={errors.renovation_history?.[index]?.description?.message}
            />
            <TextField
              label="Cost"
              type="number"
              size="small"
              sx={{ width: '25%' }}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                inputProps: { min: 0 },
              }}
              {...register(`renovation_history.${index}.cost` as const, {
                valueAsNumber: true,
                min: { value: 0, message: 'Cost cannot be negative' },
              })}
              error={!!errors.renovation_history?.[index]?.cost}
              helperText={errors.renovation_history?.[index]?.cost?.message}
            />
            <IconButton onClick={() => remove(index)} color="error" size="small" sx={{ mt: 1 }}>
              {' '}
              {/* Use remove function */}
              <DeleteIcon />
            </IconButton>
          </Box>
        ))}
      </Box>
      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        // Use append function to add a new empty entry
        onClick={() => append({ year: 0, description: '', cost: 0 })}
        sx={{ mt: 2 }}
      >
        Add Renovation
      </Button>
    </Box>
  );
};

export default RenovationHistoryStep;
